import { IconButton, Stack, useTheme } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { DynamicIcon, Tooltip } from 'src/components';
import { PersonalizedSections } from 'src/components/SidePanelPersonalization';
import { PersonalizeSidePanelModal } from 'src/components/SidePanelPersonalization/PersonalizeSidePanelModal/personalize-side-panel-modal';
import { Partner, useGroupRules, UserGroupRules } from 'src/graphql';
import { SectionHeader } from 'src/pages/Conversations/SidePanel/section-header';
import {
  floatingPanelToolSx,
  useSidePanelToolsSlot,
} from 'src/pages/Conversations/SidePanel/side-panel-tools-slot';
import { getPartnerFriendlyIdentifier } from 'src/routes/hooks';
import { useCopyToClipboard } from 'src/utils/hooks';
import { useConversationEntity } from './conversation-entity-context';

export const ConversationPropertiesPanel = () => {
  const theme = useTheme();
  const { conversation } = useConversationEntity();
  const { checkValidity } = useGroupRules();
  const canPersonalize = checkValidity([UserGroupRules.AccessToPersonalizeSidepanel]);
  const [personalizeOpen, setPersonalizeOpen] = useState(false);
  const toolsSlot = useSidePanelToolsSlot();

  const { copyToClipboard } = useCopyToClipboard();

  const handleOnClick = useCallback(async () => {
    const path = portalPaths.conversation.full(conversation.friendlyId);
    const conversationUrl = `https://${window.location.host}/${getPartnerFriendlyIdentifier(conversation.partner as Partner)}${path}`;
    await copyToClipboard(conversationUrl);
  }, [conversation, copyToClipboard]);

  const personalizeButton = canPersonalize ? (
    <Tooltip title="Personalize this view">
      <IconButton
        size="small"
        onClick={() => setPersonalizeOpen(true)}
        aria-label="Personalize this view"
        sx={
          toolsSlot ? floatingPanelToolSx : { padding: 0, width: '1.75rem', height: '1.75rem' }
        }
      >
        {toolsSlot ? (
          <DynamicIcon name="sliders-horizontal" library="lucide" size={16} />
        ) : (
          <DynamicIcon name="tune" sx={{ fontSize: '1.125rem' }} />
        )}
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <Stack gap={1}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <SectionHeader title="Properties" />

        <Stack direction="row" alignItems="center" gap={0.5}>
          {toolsSlot ? createPortal(personalizeButton, toolsSlot) : personalizeButton}
          <Tooltip title="Copy conversation link">
            <IconButton
              onClick={handleOnClick}
              sx={{
                padding: 0.5,
              }}
            >
              <DynamicIcon
                name="link"
                weight={500}
                sx={{
                  color: theme.palette.custom.OV.sys.onSurfaceVariant,
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <PersonalizedSections />

      {canPersonalize ? (
        <PersonalizeSidePanelModal
          open={personalizeOpen}
          onClose={() => setPersonalizeOpen(false)}
        />
      ) : null}
    </Stack>
  );
};
